import React from 'react';
import PropTypes from 'prop-types';
import { isMobileOnly } from 'react-device-detect';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Reports from '../Reports';
import { ReactComponent as ArrowUpRight } from '../../assets/img/arrow-up-right.svg';
import { getMyCurrentTeam } from '../../redux/selector/User';
import { leagueSetTeamInfo } from '../../redux/actions/League';

import {
  StyledContainer,
  StyledBlock,
  StyledMatchResult,
  StyledScore,
  StyledTeamsContainer,
  StyledCenterBlock,
  StyledTeam,
  StyledDatePill,
  StyledViewMore,
} from './styled';
import { LastResultPreloader } from '../ContentPreloaders';
import FallbackContentAnimation from '../FallbackContentAnimation';
import { showPopupWindow } from '../../redux/actions/Popups';
import { showMobileSidebarSection } from '../../redux/actions/Team';
import { POPUPS } from '../../service/constants';
import SectionHeading from '../SectionHeading';
import { formatMatchWhen, useDisplayTimezone } from '../../service/localTime';
import { matchOpenPath } from '../../service/liveMatch';

const LastResult = ({
  matchDate,
  away_team = null,
  our_team = null,
  isDomesticCup,
  isLoading,
  timezone = null,
  isFriendly,
  match = null,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  useDisplayTimezone();
  const myTeam = useSelector(getMyCurrentTeam);
  const reportPath = matchOpenPath(
    match || {
      match_date: matchDate,
      home_team: our_team,
      away_team,
      domestic_cup: isDomesticCup,
      friendly: isFriendly,
    }
  );
  const timezoneDateRelatedToUTC = formatMatchWhen(matchDate, {
    timezone,
    isCup: Boolean(isDomesticCup),
  });

  const showTeamInfo = (id) => {
    const { TEAM_INFO_POPUP } = POPUPS;

    dispatch(leagueSetTeamInfo({ _id: id }, isMobileOnly));
    if (isMobileOnly) {
      dispatch(showMobileSidebarSection('teamInfo'));
    } else {
      dispatch(showPopupWindow(TEAM_INFO_POPUP, TEAM_INFO_POPUP, true));
    }
  };

  const matchType = () => {
    if (isFriendly) {
      return 'Friendly';
    }
    if (isDomesticCup) {
      return 'Cup';
    }

    return 'League';
  };

  return (
    <StyledContainer>
      <SectionHeading
        title="Match Report"
        action={
          <StyledViewMore to="/league">
            View fixtures
            <ArrowUpRight />
          </StyledViewMore>
        }
      />
      <FallbackContentAnimation
        loading={isLoading}
        fallback={<LastResultPreloader />}
      >
        <StyledBlock className="last-match-section">
          <StyledDatePill>
            {timezoneDateRelatedToUTC} · {matchType()}
          </StyledDatePill>
          <StyledTeamsContainer>
            <StyledTeam
              onClick={() => our_team?._id && showTeamInfo(our_team._id)}
            >
              <img src={our_team?.symbol} alt="logo" />
              <span>{our_team?.name}</span>
            </StyledTeam>
            <StyledCenterBlock
              as={reportPath ? 'button' : 'div'}
              type={reportPath ? 'button' : undefined}
              onClick={
                reportPath
                  ? () => {
                      history.push(reportPath);
                    }
                  : undefined
              }
              title={reportPath ? 'Open match report' : undefined}
              style={
                reportPath
                  ? {
                      cursor: 'pointer',
                      border: 0,
                      padding: 0,
                      background: 'transparent',
                      font: 'inherit',
                      color: 'inherit',
                    }
                  : undefined
              }
            >
              <StyledMatchResult>
                <StyledScore
                  whichTeam={
                    myTeam?.name === our_team?.name ? 'my' : 'opponent'
                  }
                >
                  <h6>{our_team?.goals}</h6>
                </StyledScore>
                <span>-</span>
                <StyledScore
                  whichTeam={
                    myTeam?.name === away_team?.name ? 'my' : 'opponent'
                  }
                >
                  <h6>{away_team?.goals}</h6>
                </StyledScore>
              </StyledMatchResult>
            </StyledCenterBlock>
            <StyledTeam
              onClick={() => away_team?._id && showTeamInfo(away_team._id)}
            >
              <img src={away_team?.symbol} alt="logo" />
              <span>{away_team?.name}</span>
            </StyledTeam>
          </StyledTeamsContainer>
          <Reports />
        </StyledBlock>
      </FallbackContentAnimation>
    </StyledContainer>
  );
};

LastResult.propTypes = {
  matchDate: PropTypes.string.isRequired,
  away_team: PropTypes.object.isRequired,
  our_team: PropTypes.object.isRequired,
  isDomesticCup: PropTypes.oneOf([0, 1]),
  isLoading: PropTypes.bool,
  isFriendly: PropTypes.bool,
  match: PropTypes.object,
};

LastResult.defaultProps = {
  isDomesticCup: 0,
  isLoading: true,
  isFriendly: false,
  match: null,
};

export default React.memo(LastResult);
